import { FC, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { Environment } from '../../../constants/environments';
import { CountryImages, ImagesApp } from '../../../constants/images';
import { useI18nContext } from '../../../hooks/contexts/i18n.context';
import { useModalContext } from '../../../hooks/contexts/modal.context';
import { useResaleContext } from '../../../hooks/contexts/resale.context';
import { useResaleEventContext } from '../../../hooks/contexts/resaleEvent.context';
import { useUserContext } from '../../../hooks/contexts/user.context';
import useIsMobile from '../../../hooks/useIsMobile';
import { useResaleTracking } from '../../../hooks/useResaleTracking';
import { useFastPassOfferContext } from '../../../providers/FastPassOfferProvider';
import { logger } from '../../../utils/logger';
import Auth from '../../Auth/Auth';
import PillButtonColorTheme from '../../Buttons/PillButtonColorTheme';
import ResaleSummary from '../../ResaleSummary';
import ShareResaleEvent from '../../ShareResaleEvent';
import { SupportIcon } from '../../SvgCustom';
import DropDownMenu from '../AppBar/DropDownMenu';
import { LocaleSelect, SelectedLocale } from '../AppBar/LocaleSelect';
import { StyledBadgeLocale } from '../AppBar/LocaleSelect/styles.localeSelect';
import * as Style from './styles';

const ResaleAppBar: FC = () => {
  const { translate, currentLocale } = useI18nContext();
  const { resaleData, loading } = useResaleContext();
  const { event } = useResaleEventContext();
  const { user, authenticatedUser } = useUserContext();
  const { setContentModal, toggleModal, defineTitleModal } = useModalContext();
  const isMobile = useIsMobile();
  const { resaleTracking } = useResaleTracking();
  const navigate = useNavigate();
  const location = useLocation();
  const { offerId, finalPrice, isValid, loading : offerLoading } = useFastPassOfferContext();
  const isOfferResolving = !!offerId && offerLoading;
  const listingOverridePrice = (location.state as { overridePrice?: number } | null)?.overridePrice;
  const displayPrice = isValid && finalPrice !== null ? finalPrice : (listingOverridePrice ?? undefined);
  const isResalePaymentPage = !!location.pathname.match(/^\/resale\/[^/]+\/payment$/);
  const isOwnResale = location.state?.errorType === 'ownResale';
  const theme = useTheme();
  const waitingForLogin = useRef(false);

  // `currentLocale.locale` is already the canonical BCP-47 tag, which is what
  // the flag map is keyed by — no re-spelling needed.
  const locale = currentLocale.locale;

  const handleEventPageClick = () => {
    if (event?.slug) {
      // Track event page click
      if (resaleData && event) {
        resaleTracking.trackEventPageClicked(resaleData, event, user, authenticatedUser || false);
      }

      window.open(`${Environment.SITE_URL}/${event.slug}`, '_blank');
    }
  };

  const handleShareClick = () => {
    if (!event) return;

    // Track share click
    if (resaleData && event) {
      resaleTracking.trackShareClicked(resaleData, event, user, authenticatedUser || false);
    }

    defineTitleModal(translate(['SHARE_EVENT', 'TITLE']));
    setContentModal(<ShareResaleEvent event={event} />);
    toggleModal({ open : true });
  };

  const handleProceedToPayment = () => {
    if (!authenticatedUser) {
      // Track login required
      if (resaleData && event) {
        resaleTracking.trackLoginRequired(resaleData, event, 'proceed-to-payment-button');
      }

      waitingForLogin.current = true;
      defineTitleModal(translate(['INFORMATION', 'IDENTIFY_YOURSELF']));
      setContentModal(<Auth path="login" />);
      toggleModal({ open : true });
      return;
    }

    if (!user || !resaleData) {
      logger.warn('Cannot proceed to payment: missing user or resale data');
      return;
    }

    // Block seller from buying their own resale
    if (Number(user.id) === Number(resaleData.userId)) {
      navigate(`/resale/${resaleData.uuid}`, { state : { errorType : 'ownResale' } });
      return;
    }

    // Track proceed to payment clicked
    if (resaleData && event) {
      resaleTracking.trackProceedToPaymentClicked(
        resaleData,
        event,
        user,
        authenticatedUser || false,
        'proceed-to-payment-button'
      );
    }

    // Navigate to resale payment page — forward overridePrice so payment page shows spread-adjusted price
    navigate(`/resale/${resaleData.uuid}/payment`, {
      state : displayPrice !== undefined ? { overridePrice : displayPrice } : undefined,
    });
  };

  useEffect(() => {
    if (waitingForLogin.current && authenticatedUser && user) {
      // Track login completed
      if (resaleData && event) {
        resaleTracking.trackLoginCompleted(resaleData, event, user);
      }

      waitingForLogin.current = false;
      toggleModal({ open : false });

      // Auto-proceed to payment after login
      handleProceedToPayment();
    }
  }, [authenticatedUser, user, toggleModal, resaleData, event, resaleTracking]);

  // Use real data when available, fallback to placeholders
  const hasData = resaleData && event;

  if (!hasData) {
    logger.warn('[ResaleAppBar] Missing resale data or event', {
      hasResaleData : !!resaleData,
      hasEvent : !!event,
      loading,
    });
  }

  // Check if resale is available for purchase
  const isResaleAvailable = hasData && resaleData.isActive && !resaleData.isSold && !resaleData.isDeactivated;

  // Use event data or placeholders
  const eventTitle = event?.title || '-';
  const eventPoster = event?.poster?.medium || ImagesApp.svg.placeIcon;
  const eventAddress = hasData
    ? [event.place?.name, event.place?.city].filter(Boolean).join(' | ') || '-'
    : '-';

  return (
    <>
      <Style.Container id="resale-appbar-container" $hasEventSection={isResaleAvailable || false}>
        <Style.BreadcrumbSection id="resale-breadcrumb-section">
          {isResalePaymentPage ? (
            <>
              <Style.BreadcrumbInactive id="resale-breadcrumb-inactive">
                {translate(['RESALE', 'BREADCRUMB'])}
              </Style.BreadcrumbInactive>
              <Style.BreadcrumbSeparator>•</Style.BreadcrumbSeparator>
              <Style.BreadcrumbActive id="resale-breadcrumb-payment-active">
                {translate(['BREAD_CRUMB', 'TXT_4'])}
              </Style.BreadcrumbActive>
            </>
          ) : (
            <>
              <Style.BreadcrumbActive id="resale-breadcrumb-active">
                {translate(['RESALE', 'BREADCRUMB'])}
              </Style.BreadcrumbActive>
              <Style.BreadcrumbSeparator>•</Style.BreadcrumbSeparator>
              <Style.BreadcrumbInactive id="resale-breadcrumb-payment">
                {translate(['BREAD_CRUMB', 'TXT_4'])}
              </Style.BreadcrumbInactive>
            </>
          )}
        </Style.BreadcrumbSection>

        <Style.ContainerOptions id="resale-container-options">
          <PillButtonColorTheme
            onClick={() => window.open(Environment.SUPPORT_LINK, '_blank')}
            id="resale-support-button"
          >
            <img src={SupportIcon({ colorTheme : theme.colors.accent.primary })} alt="support" />
            {translate(['BUTTONS', 'SUPPORT'])}
          </PillButtonColorTheme>
          <Style.LocaleSelector id="locale-selector-container">
            <StyledBadgeLocale
              src={ImagesApp.countries[locale as keyof CountryImages]}
              alt="locale flag"
              id="locale-flag"
            />
            <DropDownMenu
              prefixDropDown={<SelectedLocale />}
              contentDropDown={<LocaleSelect />}
            />
          </Style.LocaleSelector>
        </Style.ContainerOptions>
      </Style.Container>

      {isResaleAvailable && !isOwnResale && (
        <Style.EventSection id="resale-event-section">
          {isMobile || (
            <Style.CardImage>
              <Style.PosterImage
                src={eventPoster}
                alt={eventTitle}
              />
            </Style.CardImage>
          )}
          <Style.EventDetails>
            <Style.EventTitle>{eventTitle}</Style.EventTitle>
            <Style.Info>
              <Style.Icon src={ImagesApp.svg.placeIcon} alt="location" />
              <Style.EventLocation>{eventAddress}</Style.EventLocation>
            </Style.Info>
            <Style.RowFullWidth>
              <Style.Wrapper>
                <Style.ActionLink onClick={handleEventPageClick}>
                  {translate(['STEPS', 'TXT18'])}
                </Style.ActionLink>
                <Style.ActionLink onClick={handleShareClick}>
                  {translate(['STEPS', 'TXT19'])}
                </Style.ActionLink>
              </Style.Wrapper>
            </Style.RowFullWidth>
          </Style.EventDetails>

          {!isMobile && !isResalePaymentPage && (
            <ResaleSummary
              onProceed={handleProceedToPayment}
              disabled={loading}
              overridePrice={displayPrice}
              offerPending={isOfferResolving}
            />
          )}
        </Style.EventSection>
      )}
    </>
  );
};

export default ResaleAppBar;
